// theme.ts
import { extendTheme, ThemeConfig } from "@chakra-ui/react";
import { cardTheme } from "./Card";
import { tagTheme } from "./Tag";
import { fontFamily, colors as uiColors } from "@/ui";

// Brand colors — derived from the iOS-exact ui/colors.ts so Chakra consumers
// can't drift from the design system.
const colors = {
  brand: {
    orange: uiColors.orange,
    blue: uiColors.blue,
    grey: uiColors.grey,
    yellow: uiColors.yellow,
    lightBlue: uiColors.lightBlue,
    green: uiColors.green,
    darkBlue: uiColors.darkBlue,
  },
};

// Define the global styles
const styles = {
  global: {
    body: {
      bg: "#fafafa",
      fontFamily,
    },
  },
};

// Define the fonts
const fonts = {
  heading: fontFamily,
  body: fontFamily,
};

// Define the configuration
const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

// Extend the theme with component styles
const theme = extendTheme({
  colors,
  styles,
  fonts,
  config,
  components: {
    Card: cardTheme,
    Tag: tagTheme,
    Radio: {
      baseStyle: {
        container: {
          display: "flex",
          flexDirection: "row-reverse",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#fafafa",
          borderRadius: "xl",
          padding: 2,
          paddingX: 4,
          minHeight: "36px",
        },
      },
      variants: {
        orange: {
          label: {
            fontSize: "var(--base-font-size-small)",
            ml: 0,
          },
          control: {
            border: "2px solid var(--color-grey-light)",
            _checked: {
              background: "var(--color-orange)",
              borderColor: "var(--color-orange)",

              _focus: {
                boxShadow: "none",
                borderColor: "var(--color-orange)",
              },
              _hover: {
                background: "var(--color-orange)",
                borderColor: "var(--color-orange)",
              },
            },
          },
        },
      },
    },

    Tabs: {
      variants: {
        unstyled: {
          text: {
            font: fontFamily,
            fontSize: "var(--base-font-size)",
          },
          tablist: {
            background: "white",
            padding: 0,
            paddingLeft: "19px",
            width: "fit-content",
            gap: 14,
          },
          tab: {
            fontSize: "var(--base-font-size)",
            p: 0,
            width: "6px",
            _selected: {
              fontWeight: "bold",
            },
          },
          tabpanel: {
            padding: "0",
            pt: 5,
            fontSize: "var(--base-font-size-small)",
          },
        },
        softRounded: {
          text: {
            font: fontFamily,
          },
          tablist: {
            background: "#d9d9d9",
            borderRadius: "full",
            padding: "3px 3px",
            width: "fit-content",
          },
          tab: {
            fontSize: "12px",
            padding: "3px 12px",
            _selected: {
              bg: "var(--color-grey)",
              borderRadius: "full",
              color: "white",
            },
          },
        },
      },
      sizes: {
        sm: {
          text: {
            fontSize: "12px",
          },
        },
      },
    },
    Switch: {
      variants: {
        parrotOrange: {
          track: {
            _checked: {
              bg: "var(--color-orange)",
            },
          },
        },
      },
    },
    Button: {
      variants: {
        customGhost: {
          bg: "transparent",
          border: "none",
          borderColor: "transparent",
          padding: "0",
          _hover: {
            bg: "transparent",
          },
          _active: {
            bg: "transparent",
          },
          _focus: {
            boxShadow: "none",
          },
        },
      },
    },
  },
});

export default theme;
